import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Trophy, ClipboardText, Sparkle, ChartBar } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { AdminStatCard, AdminTabBtn } from '../components/admin/AdminUI';
import AdminUsersTab from '../components/admin/AdminUsersTab';
import AdminTournamentsTab from '../components/admin/AdminTournamentsTab';
import AdminRegistrationsTab from '../components/admin/AdminRegistrationsTab';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [tournamentFilter, setTournamentFilter] = useState('pending_approval');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      logError('Failed to load admin stats:', e);
    }
  }, []);

  const loadTournaments = useCallback(async (statusFilter) => {
    try {
      const r = await axios.get(`${API}/admin/tournaments?status=${statusFilter}`, { withCredentials: true });
      setTournaments(r.data);
    } catch (e) {
      logError('Failed to load tournaments:', e);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 'users') {
      axios.get(`${API}/admin/users`, { withCredentials: true })
        .then((r) => setUsers(r.data))
        .catch((e) => logError('Failed to load users:', e));
    }
    if (tab === 'tournaments') {
      loadTournaments(tournamentFilter);
    }
    if (tab === 'registrations') {
      axios.get(`${API}/admin/registrations`, { withCredentials: true })
        .then((r) => setRegistrations(r.data))
        .catch((e) => logError('Failed to load registrations:', e));
    }
  }, [tab, tournamentFilter, loadTournaments]);

  const reloadUsers = () => {
    axios.get(`${API}/admin/users`, { withCredentials: true })
      .then((r) => setUsers(r.data))
      .catch((e) => logError('Failed to load users:', e));
    loadStats();
  };

  const reloadRegistrations = () => {
    axios.get(`${API}/admin/registrations`, { withCredentials: true })
      .then((r) => setRegistrations(r.data))
      .catch((e) => logError('Failed to load registrations:', e));
    loadStats();
  };

  const refresh = () => {
    loadStats();
    if (tab === 'tournaments') loadTournaments(tournamentFilter);
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/tournaments/${id}/approve`, {}, { withCredentials: true });
      refresh();
    } catch (e) {
      logError('Approve failed:', e);
    }
  };

  const reject = async (id) => {
    try {
      await axios.post(`${API}/admin/tournaments/${id}/reject`, {}, { withCredentials: true });
      refresh();
    } catch (e) {
      logError('Reject failed:', e);
    }
  };

  const deleteTournament = async (id) => {
    try {
      await axios.delete(`${API}/admin/tournaments/${id}`, { withCredentials: true });
      refresh();
    } catch (e) {
      logError('Delete failed:', e);
      window.alert('Delete failed');
    }
  };

  const cleanupSamples = async () => {
    if (!window.confirm('Delete all 6 sample tournaments and test player/organizer accounts? This cannot be undone.')) return;
    setCleanupBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/cleanup-sample-data`, {}, { withCredentials: true });
      window.alert(`Deleted ${data.tournaments_deleted} tournaments and ${data.users_deleted} test users`);
      refresh();
    } catch (e) {
      window.alert('Cleanup failed');
      logError('Cleanup failed:', e);
    } finally {
      setCleanupBusy(false);
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearch('');
  };

  const goToPending = () => {
    setTab('tournaments');
    setTournamentFilter('pending_approval');
  };

  return (
    <>
      <SEO title="Admin Dashboard" description="Admin control panel for Beyond Ball India." path="/admin" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-up">
            <div>
              <p className="text-gray-500 text-sm mb-1">Logged in as Admin</p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900" data-testid="admin-dashboard-title">
                Platform Control
              </h1>
            </div>
            <button
              onClick={cleanupSamples}
              disabled={cleanupBusy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-700 hover:border-red-400 hover:text-red-600 text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid="cleanup-samples-btn"
            >
              <Sparkle size={14} weight="bold" /> {cleanupBusy ? 'Cleaning…' : 'Cleanup Sample Data'}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-1 px-1 animate-fade-up delay-100">
            <AdminTabBtn id="overview" label="Overview" icon={ChartBar} active={tab === 'overview'} onClick={() => handleTabChange('overview')} />
            <AdminTabBtn id="users" label="Users" icon={Users} active={tab === 'users'} onClick={() => handleTabChange('users')} />
            <AdminTabBtn id="tournaments" label="Tournaments" icon={Trophy} active={tab === 'tournaments'} onClick={() => handleTabChange('tournaments')} />
            <AdminTabBtn id="registrations" label="Registrations" icon={ClipboardText} active={tab === 'registrations'} onClick={() => handleTabChange('registrations')} />
          </div>

          {/* Overview */}
          {tab === 'overview' && stats && (
            <div className="space-y-6 animate-fade-up delay-200">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <AdminStatCard icon={Users} label="Total Users" value={stats.total_users} onClick={() => handleTabChange('users')} color="blue" testId="stat-users" />
                <AdminStatCard icon={Trophy} label="Tournaments" value={stats.total_tournaments} onClick={() => handleTabChange('tournaments')} color="orange" testId="stat-tournaments" />
                <AdminStatCard icon={ClipboardText} label="Registrations" value={stats.total_registrations} onClick={() => handleTabChange('registrations')} color="green" testId="stat-registrations" />
                <AdminStatCard icon={Sparkle} label="Pending Review" value={stats.pending_tournaments} onClick={goToPending} color="red" testId="stat-pending" />
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
                <h2 className="text-xl font-bold mb-2">Platform health</h2>
                <p className="text-gray-300 text-sm mb-6">All systems operational. Tournament approvals are processed manually for quality control.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-bold">{stats.approved_tournaments}</div>
                    <div className="text-xs text-gray-400 uppercase">Live tournaments</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.pending_tournaments}</div>
                    <div className="text-xs text-gray-400 uppercase">Awaiting your review</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'users' && (
            <AdminUsersTab
              users={users}
              userTypeFilter={userTypeFilter}
              setUserTypeFilter={setUserTypeFilter}
              search={search}
              setSearch={setSearch}
              onRefresh={reloadUsers}
            />
          )}

          {tab === 'tournaments' && (
            <AdminTournamentsTab
              tournaments={tournaments}
              tournamentFilter={tournamentFilter}
              setTournamentFilter={setTournamentFilter}
              onApprove={approve}
              onReject={reject}
              onDelete={deleteTournament}
            />
          )}

          {tab === 'registrations' && (
            <AdminRegistrationsTab
              registrations={registrations}
              search={search}
              setSearch={setSearch}
              onRefresh={reloadRegistrations}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
